import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// material-ui
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  FormControl,
  FormControlLabel,
  Select,
  Typography,
  CircularProgress,
  Backdrop,
  Switch,
  useTheme,
  FormHelperText,
} from "@mui/material";
import { DatePicker } from "antd";
import ImageUploader from "../../../../../components/ImageUploader";

// third party
import * as Yup from "yup";
import { Formik, Field } from "formik";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";

// project import
import { openSnackbar } from "../../../../../api/snackbar";
import MainCard from "../../../../../components/MainCard";
// types
import { SnackbarProps } from "../../../../../types/snackbar";

import { USERAXIOS } from "../../../../../api/userAxios";
import { City, Country } from "../../../../../types/Country";

import {
  drivingLicenseOptions,
  genderOptions,
  militaryServiceOptions,
  maritalStatusOptions,
  mockJobSeekerProfile,
} from "./mockData";

import { JobSeeker } from "../../../../../types/jobSeeker";
import {
  useUsers,
  useUsersForAutocomplete,
} from "../../../../../hooks/query-hooks/useUsers";
import {
  useTitles,
  useTitlesForAutocomplete,
} from "../../../../../hooks/query-hooks/useTitles";
import { useCountriesForAutocomplete } from "../../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../../hooks/query-hooks/useCities";
import { useDebounce } from "../../../../../hooks";

// ==============================|| TAB - PERSONAL ||============================== //

const AddEditJobSeeker = () => {
  const navigate = useNavigate();
  const maxDate = new Date();
  const theme = useTheme();
  const params = useParams();
  const { jobseekerId } = useParams();
  const queryClient = useQueryClient();
  maxDate.setFullYear(maxDate.getFullYear() - 18);

  const [selectedNationality, setNationality] = useState(19);

  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  const [profileData, setProfileData] =
    useState<JobSeeker>(mockJobSeekerProfile);
  const [selectedMobileCode, setSelectedMobileCode] = useState("+963");
  const [isLoading, setIsLoading] = useState(false);

  // Search states for autocomplete
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [nationalitySearchTerm, setNationalitySearchTerm] = useState("");
  const [titleSearchTerm, setTitleSearchTerm] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");

  // Debounced search terms
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);
  const debouncedNationalitySearchTerm = useDebounce(
    nationalitySearchTerm,
    500
  );
  const debouncedTitleSearchTerm = useDebounce(titleSearchTerm, 500);
  const debouncedUserSearchTerm = useDebounce(userSearchTerm, 500);

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(selectedCountry, 20, debouncedCitySearchTerm);

  const { items: titles = [] } = useTitlesForAutocomplete(
    20,
    debouncedTitleSearchTerm
  );

  const { items: users = [] } = useUsersForAutocomplete(
    "JOBSEEKER",
    20,
    debouncedUserSearchTerm
  );

  const validationSchema = Yup.object().shape({
    full_name: Yup.string().required("Full Name is required"),
    full_name_arabic: Yup.object().shape({
      ar: Yup.string().required("Arabic Name is required"),
    }),
    birthdate: Yup.date().required("Birthdate is required"),
    gender: Yup.string().required("Gender is required"),
    address_english: Yup.string().required("Address English is required"),
    address_arabic: Yup.string().required("Address Arabic is required"),
    phone_number: Yup.string(),
    // email: Yup.string().email("Invalid email format"),
  });

  useEffect(() => {
    const setupProfile = async () => {
      if (countries.length === 0) return;

      if (jobseekerId) {
        await fetchJobSeekerProfile(jobseekerId);
      } else {
        const syria = countries.find((c) => c.id === 19);
        if (syria) {
          setProfileData((prev) => ({
            ...prev,
            country_id: 19,
            nationality: {
              id: syria.id,
              name: syria.name,
              name_trans: {
                ar: syria.name_trans.ar || syria.name,
              },
            },
          }));
          setSelectedCountry(19);
        }
      }
    };

    setupProfile();
  }, [jobseekerId, countries]);

  const fetchJobSeekerProfile = async (id: string) => {
    try {
      setIsLoading(true);
      const response = await USERAXIOS.get(`/jobSeekers/${id}`);
      const data = response.data.data;

      setSelectedMobileCode(data.phone_number?.split("-")[0] || "+963");

      if (data.country_id) {
        setSelectedCountry(data.country_id);

        const selectedCountryData = countries?.find(
          (c) => c.id === data.country_id
        );
        if (selectedCountryData) {
          setNationality(selectedCountryData.id);
        }

        if (data.city_id) {
          setSelectedCity(data.city_id);
        }
      }

      if (data.nationality && typeof data.nationality === "number") {
        setNationality(data.nationality);
      }

      const updatedProfileData = {
        ...data,
        address_arabic: data.address_arabic.ar,
        phone_number:
          data.phone_number?.split("-")[1] || data.phone_number || "",
        country: data.country_id || null,
        city: data.city_id || null,
      };
      setProfileData(updatedProfileData);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Failed to fetch job seeker profile",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: { color: "error" },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <section>
        <Stack direction={"row"}>
          <Stack direction={"column"}>
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                color: "primary.main",
                marginBottom: "3rem",
              }}
            >
              <FormattedMessage
                id={
                  jobseekerId
                    ? "Edit Job Seeker Profile"
                    : "Add Job Seeker Profile"
                }
              />
            </Typography>
            <Formik
              key={JSON.stringify(profileData)}
              initialValues={{
                user_id: profileData?.user_id || null,
                full_name: profileData?.full_name || "",
                full_name_arabic: {
                  ar: profileData?.full_name_arabic?.ar || "",
                },
                birthdate: profileData?.birthdate || "",
                gender: profileData?.gender || "",
                marital_status: profileData?.marital_status || "",
                military_service: profileData?.military_service || "",
                military_service_date: profileData?.military_service_date || "",
                confidetial: profileData?.confidetial || false,
                driving_license: profileData?.driving_license || "",
                driving_license_date: profileData?.driving_license_date || "",
                image: profileData?.image || "",
                address_english: profileData?.address_english || "",
                address_arabic: Array.isArray(profileData?.address_arabic)
                  ? profileData?.address_arabic[0] || ""
                  : profileData?.address_arabic || "",
                phone_number: profileData?.phone_number || "",
                nationality:
                  profileData?.nationality ||
                  countries.find((c) => c.id === 19) ||
                  null,
                title_id: profileData?.title_id || "",
                country_id: profileData?.country_id || "",
                city_id: profileData?.city_id || "",
                submit: null,
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                try {
                  const formData = new FormData();

                  formData.append("user_id", String(values.user_id || ""));
                  formData.append("full_name", values.full_name || "");
                  formData.append(
                    "full_name_arabic",
                    values.full_name_arabic?.ar || ""
                  );
                  formData.append("birthdate", values.birthdate || "");
                  formData.append("gender", values.gender || "");
                  formData.append(
                    "marital_status",
                    values.marital_status || ""
                  );
                  formData.append(
                    "military_service",
                    values.military_service || ""
                  );
                  formData.append(
                    "military_service_date",
                    values.military_service_date || ""
                  );
                  formData.append(
                    "confidetial",
                    values.confidetial ? "1" : "0"
                  );
                  formData.append(
                    "driving_license",
                    values.driving_license || ""
                  );
                  formData.append(
                    "driving_license_date",
                    values.driving_license_date || ""
                  );
                  formData.append(
                    "address_english",
                    values.address_english || ""
                  );
                  formData.append(
                    "address_arabic",
                    values.address_arabic || ""
                  );

                  formData.append("title_id", String(values.title_id || ""));
                  formData.append(
                    "nationality_id",
                    String(values.nationality?.id || 19)
                  );
                  formData.append(
                    "country_id",
                    String(selectedCountry || values.country_id)
                  );
                  formData.append(
                    "city_id",
                    String(selectedCity || values.city_id)
                  );

                  if (values?.image && typeof values.image !== "string") {
                    formData.append("image", values.image);
                  }

                  const countryCode = selectedMobileCode || "+963";
                  const phoneNumber = values.phone_number || "";

                  const phoneNumberWithCode = `${countryCode}-${phoneNumber}`;
                  formData.append("phone_number", phoneNumberWithCode);

                  if (jobseekerId) {
                    await USERAXIOS.post(
                      `/jobSeekers/${jobseekerId}`,
                      formData
                    );
                  } else {
                    await USERAXIOS.post("/jobSeekers", formData);
                  }

                  queryClient.invalidateQueries({ queryKey: ["jobSeekers"] });

                  openSnackbar({
                    open: true,
                    message: `Job seeker profile ${
                      jobseekerId ? "updated" : "created"
                    } successfully`,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "alert",
                    alert: {
                      color: "success",
                    },
                  } as SnackbarProps);

                  navigate("/jobseekers");
                } catch (error) {
                  console.error("Error saving job seeker profile:", error);
                  openSnackbar({
                    open: true,
                    message: `Error ${
                      jobseekerId ? "updating" : "creating"
                    } job seeker profile`,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "alert",
                    alert: {
                      color: "error",
                    },
                  } as SnackbarProps);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                errors,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
                touched,
                values,
              }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{ marginTop: "0px", marginBottom: "2rem" }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              Select User
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="user-select"
                                  style={{ marginBottom: "10px" }}
                                >
                                  User
                                </InputLabel>
                                <FormControl fullWidth>
                                  <InputLabel id="user-select-label">
                                    Select user
                                  </InputLabel>
                                  <Select
                                    labelId="user-select-label"
                                    id="user-select"
                                    value={values?.user_id || ""}
                                    label="Select user"
                                    onChange={(e) => {
                                      setFieldValue(
                                        "user_id",
                                        e.target.value || null
                                      );
                                    }}
                                    renderValue={(selected) => {
                                      const user = users.find(
                                        (u) => u.id === selected
                                      );
                                      return user
                                        ? user.user_name ||
                                            user.email ||
                                            String(user.id)
                                        : "";
                                    }}
                                    disabled={!!jobseekerId}
                                  >
                                    {users.map((option) => (
                                      <MenuItem
                                        key={option.id}
                                        value={option.id}
                                      >
                                        {option.user_name ||
                                          option.email ||
                                          String(option.id)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{
                            marginTop: "0px",
                            marginBottom: "2rem",
                          }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              Personal Information
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-full_name"
                                  style={{
                                    marginTop: "30px",
                                    marginBottom: "10px",
                                  }}
                                >
                                  Full Name English
                                </InputLabel>
                                <Field name="full_name">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-full_name"
                                      placeholder="Full Name English"
                                      error={!!errors.full_name}
                                      helperText={errors.full_name}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-full_name_arabic"
                                  style={{
                                    marginBottom: "10px",
                                    marginTop: "30px",
                                  }}
                                >
                                  Full Name Arabic
                                </InputLabel>
                                <Field name="full_name_arabic.ar">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-full_name_arabic"
                                      placeholder="Full Name Arabic"
                                      error={!!errors.full_name_arabic?.ar}
                                      helperText={errors.full_name_arabic?.ar}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-birthdate"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Birthdate
                                </InputLabel>
                                <Field name="birthdate">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <DatePicker
                                      style={{ width: "100%" }}
                                      value={
                                        field.value ? dayjs(field.value) : null
                                      }
                                      onChange={(date) => {
                                        form.setFieldValue(
                                          field.name,
                                          date
                                            ? date.format("YYYY-MM-DD")
                                            : null
                                        );
                                      }}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-gender"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Gender
                                </InputLabel>
                                <Field name="gender">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <FormControl
                                      fullWidth
                                      error={
                                        form.touched.gender &&
                                        Boolean(form.errors.gender)
                                      }
                                    >
                                      <Select {...field} id="gender">
                                        {genderOptions.map((option) => (
                                          <MenuItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {theme.direction === "rtl"
                                              ? option.arabic_label
                                              : option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {form.touched.gender &&
                                        form.errors.gender && (
                                          <FormHelperText>
                                            {form.errors.gender}
                                          </FormHelperText>
                                        )}
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-marital_status"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Marital Status
                                </InputLabel>
                                <Field name="marital_status">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <FormControl
                                      fullWidth
                                      error={
                                        form.touched.marital_status &&
                                        Boolean(form.errors.marital_status)
                                      }
                                    >
                                      <Select
                                        {...field}
                                        id="personal-marital_status"
                                        placeholder="Marital Status"
                                      >
                                        {maritalStatusOptions.map((option) => (
                                          <MenuItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {theme.direction === "rtl"
                                              ? option.arabic_label
                                              : option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {form.touched.marital_status &&
                                        form.errors.marital_status && (
                                          <FormHelperText>
                                            {form.errors.marital_status}
                                          </FormHelperText>
                                        )}
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-title"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Title
                                </InputLabel>
                                <Field name="title_id">
                                  {({ field }: { field: any }) => (
                                    <Select {...field} id="title" fullWidth>
                                      {titles.map((title) => (
                                        <MenuItem
                                          key={title.id}
                                          value={title.id}
                                        >
                                          {theme.direction === "rtl"
                                            ? title?.name_trans?.ar
                                            : title.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{
                            marginTop: "0px",
                            marginBottom: "2rem",
                          }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              Contact Information
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            {/* <Grid item xs={12} sm={6}>
                              <Stack>
                                <Typography>
                                  <FormattedMessage id="E-mail" />{" "}
                                </Typography>
                                <Field name="email">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="personal-email"
                                      placeholder="Email"
                                      error={ !!errors.email}
                                      helperText={ errors.email}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid> */}

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <Typography>
                                  <FormattedMessage id="Phone" />{" "}
                                </Typography>
                                <Box sx={{ display: "flex" }}>
                                  <PhoneInput
                                    country={"sy"}
                                    value={selectedMobileCode}
                                    onChange={(phone) => {
                                      setSelectedMobileCode(
                                        phone.startsWith("+")
                                          ? phone
                                          : `+${phone}`
                                      );
                                    }}
                                    inputStyle={{
                                      width: "90px",
                                      height: "56px",
                                      fontSize: "16px",
                                      borderRadius: "4px",
                                    }}
                                    containerStyle={{
                                      width: "41px",
                                      position: "relative",
                                      direction: "ltr",
                                    }}
                                    buttonStyle={{
                                      borderRadius: "4px 0 0 4px",
                                    }}
                                    dropdownStyle={{
                                      width: "300px",
                                      maxHeight: "300px",
                                      overflow: "auto",
                                      zIndex: 10000,
                                    }}
                                    searchPlaceholder="Search country..."
                                    searchNotFound="Country not found"
                                    enableSearch={true}
                                    disableSearchIcon={true}
                                    searchStyle={{
                                      width: "90%",
                                      margin: "0 auto",
                                      marginBottom: "10px",
                                    }}
                                    inputProps={{
                                      style: { display: "none" },
                                    }}
                                    countryCodeEditable={false}
                                  />
                                  <Field name="phone_number">
                                    {({ field }: { field: any }) => (
                                      <TextField
                                        {...field}
                                        fullWidth
                                        type="tel"
                                        placeholder="Phone Number"
                                        error={!!errors.phone_number}
                                        helperText={errors.phone_number}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={1}>
                                <Typography>
                                  <FormattedMessage id="Country" />
                                </Typography>
                                <Autocomplete
                                  id="country-select"
                                  options={countries || []}
                                  getOptionLabel={(option: Country) =>
                                    theme.direction === "rtl"
                                      ? option.name_trans.ar
                                      : option.name
                                  }
                                  value={
                                    countries?.find(
                                      (country) =>
                                        country.id === values.country_id
                                    ) || null
                                  }
                                  onChange={(_, newValue) => {
                                    const selectedCountryId =
                                      newValue?.id || null;

                                    setFieldValue(
                                      "country_id",
                                      selectedCountryId
                                    );
                                    setFieldValue("city_id", null);
                                    setSelectedCountry(selectedCountryId);
                                    setSelectedCity(null);
                                    // Clear search term when option is selected
                                    if (newValue) {
                                      setCountrySearchTerm("");
                                    }
                                  }}
                                  onInputChange={(
                                    event,
                                    newInputValue,
                                    reason
                                  ) => {
                                    if (reason === "input") {
                                      setCountrySearchTerm(newInputValue);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select country"
                                      error={
                                        touched.country_id &&
                                        Boolean(errors.country_id)
                                      }
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {loadingCountries && (
                                              <CircularProgress
                                                color="inherit"
                                                size={20}
                                              />
                                            )}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                  ListboxProps={{
                                    onScroll: (event) => {
                                      const listboxNode = event.currentTarget;
                                      const threshold = 100;
                                      if (
                                        listboxNode.scrollHeight -
                                          listboxNode.scrollTop <=
                                          listboxNode.clientHeight +
                                            threshold &&
                                        hasMoreCountries &&
                                        !loadingCountries
                                      ) {
                                        loadMoreCountries();
                                      }
                                    },
                                  }}
                                  loading={loadingCountries}
                                  filterOptions={(x) => x} // Disable built-in filtering
                                  noOptionsText={
                                    debouncedCountrySearchTerm.length >= 2
                                      ? "No countries found"
                                      : "Type to search countries"
                                  }
                                />
                                {touched.country_id && errors.country_id && (
                                  <FormHelperText error>
                                    {errors.country_id}
                                  </FormHelperText>
                                )}
                                {countriesError && (
                                  <FormHelperText error>
                                    Error loading countries. Please try again.
                                  </FormHelperText>
                                )}
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={1}>
                                <Typography>
                                  <FormattedMessage id="City" />
                                </Typography>
                                <Autocomplete
                                  id="city-select"
                                  options={cities}
                                  getOptionLabel={(option: City) =>
                                    theme.direction === "rtl"
                                      ? option.name_trans.ar
                                      : option.name
                                  }
                                  value={
                                    cities.find(
                                      (city) => city.id === selectedCity
                                    ) || null
                                  }
                                  onChange={(_, newValue) => {
                                    const selectedCityId = newValue?.id || null;
                                    setFieldValue("city_id", selectedCityId);
                                    setSelectedCity(selectedCityId);
                                    // Clear search term when option is selected
                                    if (newValue) {
                                      setCitySearchTerm("");
                                    }
                                  }}
                                  onInputChange={(
                                    event,
                                    newInputValue,
                                    reason
                                  ) => {
                                    if (reason === "input") {
                                      setCitySearchTerm(newInputValue);
                                    }
                                  }}
                                  disabled={!selectedCountry}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select city"
                                      error={
                                        touched.city_id &&
                                        Boolean(errors.city_id)
                                      }
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {loadingCities && (
                                              <CircularProgress
                                                color="inherit"
                                                size={20}
                                              />
                                            )}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                  ListboxProps={{
                                    onScroll: (event) => {
                                      const listboxNode = event.currentTarget;
                                      const threshold = 100;
                                      if (
                                        listboxNode.scrollHeight -
                                          listboxNode.scrollTop <=
                                          listboxNode.clientHeight +
                                            threshold &&
                                        hasMoreCities &&
                                        !loadingCities
                                      ) {
                                        loadMoreCities();
                                      }
                                    },
                                  }}
                                  loading={loadingCities}
                                  filterOptions={(x) => x} // Disable built-in filtering
                                  noOptionsText={
                                    debouncedCitySearchTerm.length >= 2
                                      ? "No cities found"
                                      : "Type to search cities"
                                  }
                                />
                                {touched.city_id && errors.city_id && (
                                  <FormHelperText error>
                                    {errors.city_id}
                                  </FormHelperText>
                                )}
                                {citiesError && (
                                  <FormHelperText error>
                                    Error loading cities. Please try again.
                                  </FormHelperText>
                                )}
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={1}>
                                <Typography>
                                  <FormattedMessage id="Nationality" />{" "}
                                </Typography>
                                <Autocomplete
                                  id="nationality-select"
                                  options={countries || []}
                                  getOptionLabel={(option: Country) =>
                                    theme.direction === "rtl"
                                      ? option.nationality_trans?.ar ||
                                        option.nationality
                                      : option.nationality
                                  }
                                  value={
                                    countries?.find(
                                      (country) =>
                                        country.id === values.nationality?.id
                                    ) || null
                                  }
                                  onChange={(_, newValue) => {
                                    const selectedCountryId =
                                      newValue?.id || null;

                                    setFieldValue(
                                      "nationality",
                                      newValue
                                        ? {
                                            id: newValue.id,
                                            name: newValue.nationality,
                                          }
                                        : null
                                    );
                                    // Clear search term when option is selected
                                    if (newValue) {
                                      setNationalitySearchTerm("");
                                    }
                                  }}
                                  onInputChange={(
                                    event,
                                    newInputValue,
                                    reason
                                  ) => {
                                    if (reason === "input") {
                                      setNationalitySearchTerm(newInputValue);
                                    }
                                  }}
                                  filterOptions={(x) => x} // Disable built-in filtering
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select nationality"
                                      error={
                                        touched.nationality &&
                                        Boolean(errors.nationality)
                                      }
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {loadingCountries && (
                                              <CircularProgress
                                                color="inherit"
                                                size={20}
                                              />
                                            )}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                  ListboxProps={{
                                    onScroll: (event) => {
                                      const listboxNode = event.currentTarget;
                                      const threshold = 100;
                                      if (
                                        listboxNode.scrollHeight -
                                          listboxNode.scrollTop <=
                                          listboxNode.clientHeight +
                                            threshold &&
                                        hasMoreCountries &&
                                        !loadingCountries
                                      ) {
                                        loadMoreCountries();
                                      }
                                    },
                                  }}
                                  loading={loadingCountries}
                                  noOptionsText={
                                    debouncedNationalitySearchTerm.length >= 2
                                      ? "No nationalities found"
                                      : "Type to search nationalities"
                                  }
                                />
                                {touched.nationality && errors.nationality && (
                                  <FormHelperText error>
                                    {errors.nationality.name}
                                  </FormHelperText>
                                )}
                                {countriesError && (
                                  <FormHelperText error>
                                    Error loading nationalities. Please try
                                    again.
                                  </FormHelperText>
                                )}
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-address_english"
                                  style={{ marginBottom: "10px" }}
                                >
                                  Address English
                                </InputLabel>
                                <Field name="address_english">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="personal-address_english"
                                      placeholder="Address English"
                                      error={!!errors.address_english}
                                      helperText={errors.address_english}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-address_arabic"
                                  style={{ marginBottom: "10px" }}
                                >
                                  Address Arabic
                                </InputLabel>
                                <Field name="address_arabic">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="personal-address_arabic"
                                      placeholder="Address Arabic"
                                      error={!!errors.address_arabic}
                                      helperText={errors.address_arabic}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{
                            marginTop: "0px",
                            marginBottom: "2rem",
                          }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              Additional Information
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-military_service"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Military Service
                                </InputLabel>
                                <Field name="military_service">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <FormControl
                                      fullWidth
                                      error={
                                        form.touched.military_service &&
                                        Boolean(form.errors.military_service)
                                      }
                                    >
                                      <Select {...field} id="military_service">
                                        {militaryServiceOptions.map(
                                          (option) => (
                                            <MenuItem
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {theme.direction === "rtl"
                                                ? option.arabic_label
                                                : option.label}
                                            </MenuItem>
                                          )
                                        )}
                                      </Select>
                                      {form.touched.military_service &&
                                        form.errors.military_service && (
                                          <FormHelperText>
                                            {form.errors.military_service}
                                          </FormHelperText>
                                        )}
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-military_service_date"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Military Service Date
                                </InputLabel>
                                <Field name="military_service_date">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <DatePicker
                                      style={{ width: "100%" }}
                                      value={
                                        field.value ? dayjs(field.value) : null
                                      }
                                      onChange={(date) => {
                                        form.setFieldValue(
                                          field.name,
                                          date
                                            ? date.format("YYYY-MM-DD")
                                            : null
                                        );
                                      }}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-driving_license"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Driving License
                                </InputLabel>
                                <Field name="driving_license">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <FormControl
                                      fullWidth
                                      error={
                                        form.touched.driving_license &&
                                        Boolean(form.errors.driving_license)
                                      }
                                    >
                                      <Select {...field} id="driving_license">
                                        {drivingLicenseOptions.map((option) => (
                                          <MenuItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {theme.direction === "rtl"
                                              ? option.arabic_label
                                              : option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {form.touched.driving_license &&
                                        form.errors.driving_license && (
                                          <FormHelperText>
                                            {form.errors.driving_license}
                                          </FormHelperText>
                                        )}
                                    </FormControl>
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-driving_license_date"
                                  style={{
                                    marginBottom: "10px",
                                  }}
                                >
                                  Driving License Date
                                </InputLabel>
                                <Field name="driving_license_date">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <DatePicker
                                      style={{ width: "100%" }}
                                      value={
                                        field.value ? dayjs(field.value) : null
                                      }
                                      onChange={(date) => {
                                        form.setFieldValue(
                                          field.name,
                                          date
                                            ? date.format("YYYY-MM-DD")
                                            : null
                                        );
                                      }}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Stack>
                                <Typography>
                                  <FormattedMessage id="Profile Image" />
                                </Typography>
                                <ImageUploader
                                  id="jobseeker-image"
                                  previewUrl={
                                    typeof values.image === "string"
                                      ? values.image
                                      : values.image
                                      ? URL.createObjectURL(values.image)
                                      : null
                                  }
                                  onChange={(e, setFieldValue) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      setFieldValue("image", file);
                                    }
                                  }}
                                  setFieldValue={setFieldValue}
                                  label="Click to upload profile image"
                                />
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="personal-confidetial"
                                  style={{
                                    marginBottom: "10px",
                                    marginTop: "30px",
                                  }}
                                >
                                  Confidential
                                </InputLabel>
                                <Box>
                                  <Field name="confidetial">
                                    {({ field }: { field: any }) => (
                                      <FormControlLabel
                                        control={
                                          <Box>
                                            <Switch
                                              checked={field.value}
                                              onChange={(e) => {
                                                field.onChange({
                                                  target: {
                                                    name: field.name,
                                                    value: e.target.checked,
                                                  },
                                                });
                                              }}
                                              name="confidetial"
                                            />
                                          </Box>
                                        }
                                        label={field.value ? "Yes" : "No"}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                    </Grid>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    marginTop="2rem"
                  >
                    <Grid container justifyContent="center" alignItems="center">
                      <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                        <Grid item md={2} />
                        <Grid item md={4}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => navigate("/jobseekers")}
                          >
                            <FormattedMessage id="Cancel" />
                          </Button>
                        </Grid>
                        <Grid item md={4}>
                          <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            <FormattedMessage
                              id={jobseekerId ? "Update" : "Create"}
                            />
                          </Button>
                        </Grid>
                        <Grid item md={2} />
                      </Stack>
                    </Grid>
                  </Stack>
                </form>
              )}
            </Formik>
          </Stack>
        </Stack>
      </section>
    </>
  );
};

export default AddEditJobSeeker;
